import React, { useEffect, useMemo, useState } from "react";
import EventCard from "../../components/EventManagement/NewFeed";
import ImageLightbox from "../../components/EventManagement/Component/ImageLightBox";
import {
  convertPosts,
  filterByLatest,
  sortActiveFeed,
  sortByEventStartDate,
} from "../../components/EventManagement/utils";
import { useHits } from "react-instantsearch-hooks-web";
import CreatePost from "../../components/EventManagement/CreatePost";
import CreateEvent from "../../components/EventManagement/CreateEvent";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

type FeedSection = "active" | "past" | "archived";

type FeedItem = {
  section: FeedSection;
  data: any;
};

function startOfToday() {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
}

function getEventStartDate(item: any): Date | null {
  if (!item?.eventStartDate) return null;

  // Algolia may store ms number; Firestore/export may store { seconds }
  if (typeof item.eventStartDate === "object" && item.eventStartDate.seconds) {
    return new Date(item.eventStartDate.seconds * 1000);
  }

  const date = new Date(item.eventStartDate);
  return Number.isNaN(date.getTime()) ? null : date;
}

function getPaginationRange(
  currentPage: number,
  totalPages: number,
): (number | "ellipsis")[] {
  const padding = 1;
  const pages: (number | "ellipsis")[] = [];

  if (totalPages <= 7) {
    for (let i = 0; i < totalPages; i++) pages.push(i);
    return pages;
  }

  pages.push(0);
  const start = Math.max(1, currentPage - padding);
  const end = Math.min(totalPages - 2, currentPage + padding);
  if (start > 1) pages.push("ellipsis");
  for (let i = start; i <= end; i++) pages.push(i);
  if (end < totalPages - 2) pages.push("ellipsis");
  pages.push(totalPages - 1);
  return pages;
}

const EventManagementAdmin = () => {
  const { hits } = useHits();
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [lightboxState, setLightboxState] = useState({
    isOpen: false,
    data: null,
    initialIndex: 0,
  });

  /**
   * Bucket all filtered Algolia hits, then order Active → Past → Archived
   * so upcoming events always appear before pagination is applied.
   */
  const orderedFeed: FeedItem[] = useMemo(() => {
    const data = hits || [];
    const today = startOfToday();

    const active: any[] = [];
    const past: any[] = [];
    const archived: any[] = [];

    data.forEach((item: any) => {
      if (item.status === "archived") {
        archived.push(item);
        return;
      }

      if (item.post_type === "post" && item.status === "active") {
        active.push(item);
        return;
      }

      if (item.post_type === "event") {
        const eventDate = getEventStartDate(item);
        if (!eventDate) return;

        if (eventDate < today) {
          past.push(item);
        } else if (item.status === "active") {
          active.push(item);
        }
      }
    });

    const activeSorted = convertPosts(sortActiveFeed(active));
    const pastSorted = convertPosts(sortByEventStartDate(past, "desc"));
    const archivedSorted = convertPosts(filterByLatest(archived));

    const feed: FeedItem[] = [
      ...activeSorted.map((data: any) => ({ section: "active" as const, data })),
      ...pastSorted.map((data: any) => ({ section: "past" as const, data })),
      ...archivedSorted.map((data: any) => ({
        section: "archived" as const,
        data,
      })),
    ];
    return feed;
  }, [hits]);

  const totalPages = Math.max(1, Math.ceil(orderedFeed.length / pageSize));
  const safePage = Math.min(currentPage, totalPages - 1);

  const paginatedFeed: FeedItem[] = useMemo(() => {
    const start = safePage * pageSize;
    return orderedFeed.slice(start, start + pageSize);
  }, [orderedFeed, safePage, pageSize]);

  const hitIds = useMemo(
    () => (hits || []).map((h: any) => h.objectID).join(","),
    [hits],
  );

  useEffect(() => {
    setCurrentPage(0);
  }, [hitIds]);

  useEffect(() => {
    setCurrentPage(0);
  }, [pageSize]);

  const paginationRange: (number | "ellipsis")[] = useMemo(
    () => getPaginationRange(safePage, totalPages),
    [safePage, totalPages],
  );

  const handleOpenLightbox = (data: any, index: number) => {
    setLightboxState({
      isOpen: true,
      data,
      initialIndex: index,
    });
    document.body.style.overflow = "hidden";
  };

  const handleCloseLightbox = () => {
    setLightboxState((prev: any) => ({ ...prev, isOpen: false }));
    document.body.style.overflow = "auto";
  };

  const sectionHasItems = (section: FeedSection) =>
    orderedFeed.some((item: FeedItem) => item.section === section);

  const shouldShowSectionHeader = (
    section: FeedSection,
    index: number,
  ): boolean => {
    if (index === 0) {
      // Show header when this page starts a non-active section, or when
      // the section exists but has no items on earlier conceptual slots.
      if (section === "active") return false;
      return true;
    }
    return paginatedFeed[index - 1]?.section !== section;
  };

  const renderSectionHeader = (section: FeedSection) => {
    if (section === "active") return null;
    if (section === "past") {
      return (
        <h1 className="mt-5 text-2xl font-semibold text-instant-teams-blue-Main">
          Past Events
        </h1>
      );
    }
    return (
      <h1 className="mt-5 text-2xl font-semibold text-instant-teams-blue-Main">
        Archived Events & Posts
      </h1>
    );
  };

  const emptyActive =
    !sectionHasItems("active") &&
    paginatedFeed.every((item: FeedItem) => item.section !== "active");

  return (
    <>
      <div className="flex w-full justify-center space-x-5">
        <div className="flex items-center space-x-5">
          <div>Results per page:</div>
          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className="h-6 py-0 text-xs"
          >
            {PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
          <div className="flex items-center justify-start space-x-5">
            <CreatePost />
            <CreateEvent />
          </div>
        </div>
      </div>

      <div className="flex h-[100vh] w-full justify-center">
        <div className="w-[50%]">
          <div className="h-[80vh] overflow-y-auto">
            {orderedFeed.length === 0 ? (
              <div className="mb-5 mt-2 border bg-white p-6 text-center shadow-sm">
                No events & posts
              </div>
            ) : (
              <>
                {emptyActive && safePage === 0 && (
                  <div className="mb-5 mt-2 border bg-white p-6 text-center shadow-sm">
                    No events & posts
                  </div>
                )}

                {paginatedFeed.map((item: FeedItem, index: number) => {
                  const key =
                    item.data?.id ||
                    item.data?.objectID ||
                    `${item.section}-${index}`;
                  return (
                    <div key={key} className="pr-2">
                      {shouldShowSectionHeader(item.section, index) &&
                        renderSectionHeader(item.section)}
                      <div className="mt-5">
                        <EventCard
                          data={item.data}
                          pastEvent={item.section !== "active"}
                          onOpenLightbox={handleOpenLightbox}
                        />
                      </div>
                    </div>
                  );
                })}

                {safePage === 0 && !sectionHasItems("past") && (
                  <div className="mt-5">
                    <h1 className="text-2xl font-semibold text-instant-teams-blue-Main">
                      Past Events
                    </h1>
                    <div className="mb-5 mt-2 border bg-white p-6 text-center shadow-sm">
                      No past events
                    </div>
                  </div>
                )}

                {safePage === 0 && !sectionHasItems("archived") && (
                  <div className="mt-5">
                    <h1 className="text-2xl font-semibold text-instant-teams-blue-Main">
                      Archived Events & Posts
                    </h1>
                    <div className="mb-5 mt-2 border bg-white p-6 text-center shadow-sm">
                      No archived events & posts
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {totalPages > 1 && (
        <div className="mt-[-120px] flex justify-center gap-1 pb-6">
          <button
            type="button"
            disabled={safePage === 0}
            onClick={() => setCurrentPage((p: number) => Math.max(0, p - 1))}
            className="mx-1 flex h-8 w-12 items-center justify-center rounded bg-white text-sm disabled:opacity-40"
          >
            ‹
          </button>
          {paginationRange.map((item: number | "ellipsis", idx: number) =>
            item === "ellipsis" ? (
              <span
                key={`ellipsis-${idx}`}
                className="mx-1 flex h-8 w-12 items-center justify-center text-sm text-gray-400"
              >
                …
              </span>
            ) : (
              <button
                key={item}
                type="button"
                onClick={() => setCurrentPage(item)}
                className={`mx-1 flex h-8 w-12 items-center justify-center rounded bg-white text-sm ${
                  item === safePage ? "scale-125 border border-black" : ""
                }`}
              >
                {item + 1}
              </button>
            ),
          )}
          <button
            type="button"
            disabled={safePage >= totalPages - 1}
            onClick={() =>
              setCurrentPage((p: number) => Math.min(totalPages - 1, p + 1))
            }
            className="mx-1 flex h-8 w-12 items-center justify-center rounded bg-white text-sm disabled:opacity-40"
          >
            ›
          </button>
        </div>
      )}

      <ImageLightbox
        isOpen={lightboxState.isOpen}
        onClose={handleCloseLightbox}
        data={lightboxState.data}
        initialIndex={lightboxState.initialIndex}
      />
    </>
  );
};

export default EventManagementAdmin;
